import { translate } from "app/filters/i18nFilter";

export const entities = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    URL: "URLs",
    domain: "domains",
    website: "websites",
} as const;

export type EntityKey = keyof typeof entities;

export enum EGraphGranularities {
    DAILY,
    WEEKLY,
    MONTHLY,
}

export const graphGranularityToString = {
    [EGraphGranularities.DAILY]: "Daily",
    [EGraphGranularities.WEEKLY]: "Weekly",
    [EGraphGranularities.MONTHLY]: "Monthly",
};

export const TABLE_DASHBOARD_METRIC = "TrafficBreakdown";
export const GRAPH_DASHBOARD_METRIC = "ClicksOverTime";
export const entity: EntityKey = "domain";

import type { TextMode } from "app/common/keyword-research/domains-table/tableTypes";

export const KEYWORD_ANALYSIS_PREFIX = "keyword.analysis.table.header.column";

export const TABLE_COLUMN_SELECTION = "selection";
export const TABLE_COLUMN_DOMAIN = "Domain";
export const TABLE_COLUMN_URL = "url";
export const TABLE_COLUMN_KEYWORD = "Keyword";
export const TABLE_COLUMN_KEYWORDS = "Keywords";
export const TABLE_COLUMN_SHARE = "Share";
export const TABLE_COLUMN_CHANGE = "Change";
export const TABLE_COLUMN_WEB_SOURCE_SHARE = "WebSourceShare";
export const TABLE_COLUMN_ORGANIC_CLICKS_SHARE = "OrganicClicksShare";
export const TABLE_COLUMN_POSITION = "Position";
export const TABLE_COLUMN_SERP_FEATURES = "SerpFeatures";
export const TABLE_COLUMN_TOP_KEYWORD = "TopKeyword";
export const TABLE_COLUMN_TOTAL_MONTHLY_VOLUME = "totalMonthlyVolume";
export const TABLE_COLUMN_TOTAL_WINDOW_VOLUME = "totalWindowVolume";
export const TABLE_COLUMN_MONTHLY_VOLUME = "monthlyVolume";
export const TABLE_COLUMN_WINDOW_VOLUME = "windowVolume";
export const TABLE_COLUMN_TOP_INTENT = "TopIntent";
export const TABLE_COLUMN_DIFFICULTY = "Difficulty";
export const TABLE_COLUMN_AVG_COMPETITION = "AvgCompetition";
export const TABLE_COLUMN_COMPETITION = "Competition";
export const TABLE_COLUMN_AVG_DIFFICULTY = "AvgDifficulty";
export const TABLE_COLUMN_INTENT = "Intent";
export const TABLE_COLUMN_CPC = "Cpc";
export const TABLE_COLUMN_TOP_URL = "TopUrl";

export const columnNamesAndTooltipsMap: Record<string, Record<TextMode, string>> = {
    [TABLE_COLUMN_DOMAIN]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_DOMAIN}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_DOMAIN}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_DOMAIN}.title`,
    },
    [TABLE_COLUMN_URL]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_URL}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_URL}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_URL}.title`,
    },
    [TABLE_COLUMN_KEYWORD]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_KEYWORD}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_KEYWORD}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_KEYWORD}.title`,
    },
    [TABLE_COLUMN_KEYWORDS]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_KEYWORDS}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_KEYWORDS}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_KEYWORDS}.title`,
    },
    [TABLE_COLUMN_SHARE]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_SHARE}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_SHARE}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_SHARE}.title`,
    },
    [TABLE_COLUMN_CHANGE]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_CHANGE}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_CHANGE}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_CHANGE}.title`,
    },
    [TABLE_COLUMN_WEB_SOURCE_SHARE]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_WEB_SOURCE_SHARE}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_WEB_SOURCE_SHARE}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_WEB_SOURCE_SHARE}.title`,
    },
    [TABLE_COLUMN_ORGANIC_CLICKS_SHARE]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_ORGANIC_CLICKS_SHARE}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_ORGANIC_CLICKS_SHARE}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_ORGANIC_CLICKS_SHARE}.title`,
    },
    [TABLE_COLUMN_POSITION]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_POSITION}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_POSITION}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_POSITION}.title`,
    },
    [TABLE_COLUMN_SERP_FEATURES]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_SERP_FEATURES}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_SERP_FEATURES}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_SERP_FEATURES}.title`,
    },
    [TABLE_COLUMN_TOP_KEYWORD]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOP_KEYWORD}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOP_KEYWORD}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOP_KEYWORD}.title`,
    },
    [TABLE_COLUMN_MONTHLY_VOLUME]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_MONTHLY_VOLUME}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_MONTHLY_VOLUME}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_MONTHLY_VOLUME}.title`,
    },
    [TABLE_COLUMN_WINDOW_VOLUME]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_WINDOW_VOLUME}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_WINDOW_VOLUME}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_WINDOW_VOLUME}.title`,
    },
    [TABLE_COLUMN_TOTAL_MONTHLY_VOLUME]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOTAL_MONTHLY_VOLUME}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOTAL_MONTHLY_VOLUME}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOTAL_MONTHLY_VOLUME}.title`,
    },
    [TABLE_COLUMN_TOTAL_WINDOW_VOLUME]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOTAL_WINDOW_VOLUME}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOTAL_WINDOW_VOLUME}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOTAL_WINDOW_VOLUME}.title`,
    },
    [TABLE_COLUMN_TOP_INTENT]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOP_INTENT}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOP_INTENT}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOP_INTENT}.title`,
    },
    [TABLE_COLUMN_DIFFICULTY]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_DIFFICULTY}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_DIFFICULTY}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_DIFFICULTY}.title`,
    },
    [TABLE_COLUMN_AVG_COMPETITION]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_AVG_COMPETITION}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_AVG_COMPETITION}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_AVG_COMPETITION}.title`,
    },
    [TABLE_COLUMN_COMPETITION]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_COMPETITION}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_COMPETITION}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_COMPETITION}.title`,
    },
    [TABLE_COLUMN_AVG_DIFFICULTY]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_AVG_DIFFICULTY}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_AVG_DIFFICULTY}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_AVG_DIFFICULTY}.title`,
    },
    [TABLE_COLUMN_INTENT]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_INTENT}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_INTENT}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_INTENT}.title`,
    },
    [TABLE_COLUMN_CPC]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_CPC}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_CPC}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_CPC}.title`,
    },
    [TABLE_COLUMN_TOP_URL]: {
        single: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOP_URL}.single.tooltip`,
        group: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOP_URL}.group.tooltip`,
        title: `${KEYWORD_ANALYSIS_PREFIX}.${TABLE_COLUMN_TOP_URL}.title`,
    },
};

export const visibleColumns: string[] = [
    TABLE_COLUMN_SELECTION,
    TABLE_COLUMN_DOMAIN,
    TABLE_COLUMN_KEYWORDS,
    TABLE_COLUMN_SHARE,
    TABLE_COLUMN_CHANGE,
    TABLE_COLUMN_WEB_SOURCE_SHARE,
    TABLE_COLUMN_ORGANIC_CLICKS_SHARE,
    TABLE_COLUMN_POSITION,
    TABLE_COLUMN_SERP_FEATURES,
    TABLE_COLUMN_TOP_URL,
];

export const visibleColumnsEnrich: string[] = [
    TABLE_COLUMN_SELECTION,
    TABLE_COLUMN_KEYWORD,
    TABLE_COLUMN_SHARE,
    TABLE_COLUMN_CHANGE,
    TABLE_COLUMN_INTENT,
    TABLE_COLUMN_DIFFICULTY,
    TABLE_COLUMN_COMPETITION,
    TABLE_COLUMN_CPC,
    TABLE_COLUMN_WEB_SOURCE_SHARE,
    TABLE_COLUMN_ORGANIC_CLICKS_SHARE,
    TABLE_COLUMN_TOP_URL,
    TABLE_COLUMN_POSITION,
    TABLE_COLUMN_SERP_FEATURES,
    TABLE_COLUMN_MONTHLY_VOLUME,
    TABLE_COLUMN_WINDOW_VOLUME,
];

export const dropDownColumnsTexts = {
    get openUrlTitle() {
        return translate("search.pages.table.serp.columns.open");
    },
    get copyUrlTitle() {
        return translate("search.pages.table.serp.columns.copy");
    },
    get exploreUrlKeywords() {
        return translate("search.pages.serp.table.columns.explore");
    },
    get successToast() {
        return translate("keywordResearch.pages.serp.table.copy.success");
    },
    get errorToast() {
        return translate("keywordResearch.pages.serp.table.copy.error");
    },
};
